import invariant from 'invariant';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type {
  OrgFieldsValues,
  OrgFormValues,
} from '#/components/Form/Fields/Org/getOrgFields';
import { getOrgFields } from '#/components/Form/Fields/Org/getOrgFields';
import { useFormContext } from '#/components/Form/FormContext';
import { MultiStepForm } from '#/components/Form/MultiStepForm/MultiStepForm';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import type { OrgDashboardApiOrg } from '#/modules/orgDashboard/api/types/OrgDashboardApiOrg';
import { useOrgDashboardOrgContext } from '#/modules/orgDashboard/contexts/OrgDashboardOrgContext';
import { OrgDashboardMembershipCallout } from '#/modules/orgDashboard/pages/membership/view/OrgDashboardMembershipCallout';
import type { StaffApiOrg } from '#/modules/staff/listings/orgs/api/types/StaffApiOrg';

import { getCreateFieldSteps } from './getCreateFieldSteps';
import { getEditFields } from './getEditFields';
import { getStaffFields } from './getStaffFields';

type Props = {
  intro?: ReactNode;
  orgName?: string;
  org?: OrgDashboardApiOrg | StaffApiOrg;
  simple?: boolean;
  submitText?: string;
  showVatNumber?: boolean;
  isStaff?: boolean;
  isEdit?: boolean;
  trackingTitle?: string;
  title?: string;
};

export function OrgForm({
  intro,
  orgName,
  org,
  simple,
  submitText,
  showVatNumber = false,
  isStaff = false,
  isEdit = false,
  trackingTitle,
  title,
}: Props) {
  const { i18n } = useI18n();

  const { values, updateValue } = useFormContext<OrgFormValues>();
  const { is501c3, orgType, address } = values;
  const isInUS = address?.country === 'US';

  const [orgMatch, setOrgMatch] = useState<{ id: string; name: string } | null>(
    null,
  );

  const isPending = !org?.isApproved;
  const hasOrgLogo = org?.logo !== null;
  const orgId = org?.id;

  const steps = useMemo(() => {
    if (isStaff) return getStaffFields(i18n, values as OrgFieldsValues, isInUS);

    if (isEdit) {
      invariant(org, 'Org should be defined when editing org');
      const expirationDate = org.membershipExpiration
        ? new Date(org.membershipExpiration)
        : null;
      const isExpired = expirationDate && expirationDate < new Date();
      const canRenew =
        org.orgType !== ApiOrgType.CORPORATION &&
        expirationDate &&
        expirationDate < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      const membershipCallout = (
        <OrgDashboardMembershipCallout
          canRenew={canRenew}
          expirationDate={expirationDate}
          isExpired={isExpired}
          setError={() => {}}
          linkToMembership
        />
      );
      const donateEnabled = expirationDate && expirationDate > new Date();
      return getEditFields(
        i18n,
        values,
        showVatNumber,
        org.orgType,
        isPending,
        donateEnabled,
        membershipCallout,
      );
    }
    return getCreateFieldSteps(i18n, values, isInUS);
  }, [i18n, org, isStaff, values, isInUS, isEdit, showVatNumber, isPending]);

  const { setOrg } = useOrgDashboardOrgContext();

  const fields = getOrgFields({
    i18n,
    orgId,
    orgName,
    values,
    orgMatch,
    setOrgMatch,
    simple,
    isStaff,
    isEdit,
    hasOrgLogo,
    onUpdateOrg: setOrg,
  });

  useEffect(() => {
    if (values.relatedJobCount === null) {
      updateValue('relatedJobAreas', false);
    } else if (values.relatedJobCount !== undefined) {
      updateValue('relatedJobAreas', true);
    }
  }, [updateValue, values.relatedJobCount]);

  useEffect(() => {
    // TODO: Fix this from eslint v9 update
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (values.relatedJobAreas && orgType !== 'CORPORATION') {
      updateValue('relatedJobAreas', undefined);
    }
  }, [orgType, updateValue, values.relatedJobAreas]);

  useEffect(() => {
    if (is501c3) {
      updateValue('orgType', ApiOrgType.NONPROFIT);
    }
  }, [is501c3, updateValue]);

  return (
    <MultiStepForm
      disableConfirmTabClose={Boolean(orgMatch)}
      trackingTitle={trackingTitle}
      title={title}
      intro={intro}
      steps={steps}
      fields={fields}
      submitText={submitText}
      hideHeader={isEdit || isStaff}
      noButtons={isEdit || isStaff}
      singlePage={isEdit || isStaff}
      large={!isEdit}
    />
  );
}
